import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData } from 'chart.js';
import { SiteReportService, EventData, PivotData } from '../service/site-report.service';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-site-report-dashboard',
  templateUrl: './site-report-dashboard.component.html',
  styleUrls: ['./site-report-dashboard.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule
  ]
})
export class SiteReportDashboardComponent implements OnInit, OnChanges {
  @Input() eventData: EventData[] = [];
  @Input() siteName: string = '';

  private isUpdating = false;
  private updateTimeout: any = null;
  public chartsReady = false;
  // Chart instances
  private eventTypeChart: Chart | null = null;
  private timeDistributionChart: Chart | null = null;
  private timeLevelStackedChart: Chart | null = null; // Added for stacked bar chart
  private cameraDistributionChart: Chart | null = null;
  private levelDistributionChart: Chart | null = null;
  private actionDistributionChart: Chart | null = null;
  private levelActionLineChart: Chart | null = null;
  private consolidatedLevelChart: Chart | null = null;

  // Chart canvas references
  @ViewChild('eventTypeCanvas', { static: false }) eventTypeCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('timeDistributionCanvas', { static: false }) timeDistributionCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('timeLevelStackedCanvas', { static: false }) timeLevelStackedCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('cameraDistributionCanvas', { static: false }) cameraDistributionCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('levelDistributionCanvas', { static: false }) levelDistributionCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('actionDistributionCanvas', { static: false }) actionDistributionCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('levelActionLineCanvas', { static: false }) levelActionLineCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('consolidatedLevelCanvas', { static: false }) consolidatedLevelCanvas!: ElementRef<HTMLCanvasElement>;

  // // Chart configurations
  // public eventTypeChartData: any = {
  //   labels: [],
  //   datasets: [{
  //     data: [],
  //     backgroundColor: [
  //       '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  //       '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
  //     ],
  //     borderColor: [
  //       '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  //       '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
  //     ],
  //     borderWidth: 1
  //   }]
  // };

  // public timeDistributionChartData: any = {
  //   labels: [],
  //   datasets: [{
  //     label: 'Events by Hour',
  //     data: [],
  //     backgroundColor: ['#36A2EB'],
  //     borderColor: ['#2693E6'],
  //     borderWidth: 1
  //   }]
  // };

  // public cameraDistributionChartData: any = {
  //   labels: [],
  //   datasets: [{
  //     data: [],
  //     backgroundColor: [
  //       '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  //       '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
  //     ],
  //     borderColor: [
  //       '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  //       '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
  //     ],
  //     borderWidth: 1
  //   }]
  // };

  // Chart data
  public eventTypeChartData: any = { labels: [], datasets: [] };
  public timeDistributionChartData: any = { labels: [], datasets: [] };
  public cameraDistributionChartData: any = { labels: [], datasets: [] };


  // Pivot tables
  public cameraLevelPivot: PivotData | null = null;
  public cameraMonthPivot: PivotData | null = null;
  public timeMonthPivot: PivotData | null = null;
  public eventTypeBreakdown: any[] = [];
  public levelDistribution: any[] = [];
  public timeDistribution: any[] = [];
  public timeLevelDistribution: any = null; // Added for stacked bar chart
  public cameraDistribution: any[] = [];
  public actionDistribution: any[] = [];
  public levelActionLineData: any = null;
  public consolidatedLevelData: any = null;

  // Summary statistics
  public summaryStats = {
    totalEvents: 0,
    uniqueCameras: 0,
    uniqueEventTypes: 0,
    dateRange: '',
    averageEventsPerDay: 0
  };

  constructor(
    private siteReportService: SiteReportService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.updateCharts();
  }

  ngOnDestroy(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventData'] && this.eventData.length > 0 && !this.isUpdating) {
      this.isUpdating = true;
      this.chartsReady = false; // Reset charts ready flag
      this.cdr.detectChanges(); // Force change detection
      
      // Clear any existing timeout
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
      
      // Use setTimeout to ensure all data is accumulated and DOM is ready
      this.updateTimeout = setTimeout(() => {
        this.processData();
        this.cdr.detectChanges(); // Force change detection after data processing
        // Add another timeout to ensure data processing is complete
        setTimeout(() => {
          this.updateCharts();
          this.cdr.detectChanges(); // Force change detection after chart updates
          // Mark charts as ready after a final delay
          setTimeout(() => {
            this.chartsReady = true;
            this.cdr.detectChanges(); // Force change detection when charts are ready
            this.isUpdating = false;
          }, 300);
        }, 200);
      }, 100);
    }
  }

  private processData(): void {
    try {
      if (!this.eventData || this.eventData.length === 0) {
        console.log('No event data to process');
        return;
      }

      console.log('Processing event data:', this.eventData.length, 'events');
      
      // Debug timestamp values
      console.log('Sample timestamps:', this.eventData.slice(0, 5).map(item => ({
        eventId: item.eventId,
        timestamp: item.timestamp,
        eventtimestampstr: item.eventtimestampstr,
        eventtimestrinct: item.eventtimestrinct,
        actiontimestring: item.actiontimestring,
        usedTimestamp: item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring,
        parsed: new Date(item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring),
        isValid: !isNaN(new Date(item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring).getTime())
      })));

      // Create pivot tables
      this.cameraLevelPivot = this.siteReportService.createPivotTable(
        this.eventData, 'cameraName', 'currentLevelId'
      );

      // Create time distribution pivot (by hour and month)
      const timeData = this.eventData.map(item => {
        try {
          // Try different timestamp fields in order of preference
          const timestampStr = item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring;
          
          if (!timestampStr || timestampStr.trim() === '') {
            console.warn('No valid timestamp found for item:', item.eventId);
            return {
              ...item,
              hour: '00:00',
              month: 'Unknown'
            };
          }
          
          const timestamp = new Date(timestampStr);
          if (isNaN(timestamp.getTime())) {
            console.warn('Invalid timestamp:', timestampStr, 'for item:', item.eventId);
            return {
              ...item,
              hour: '00:00',
              month: 'Unknown'
            };
          }
          
          return {
            ...item,
            hour: `${timestamp.getHours().toString().padStart(2, '0')}:00`,
            month: timestamp.toLocaleString('default', { month: 'short' })
          };
        } catch (error) {
          console.warn('Error processing timestamp for item:', item.eventId, error);
          return {
            ...item,
            hour: '00:00',
            month: 'Unknown'
          };
        }
      });
      
      this.timeMonthPivot = this.siteReportService.createPivotTable(
        timeData, 'hour', 'month'
      );

      // Create camera-month pivot table
      this.cameraMonthPivot = this.siteReportService.createPivotTable(
        timeData, 'cameraName', 'month'
      );

      // Get breakdowns
      this.eventTypeBreakdown = this.siteReportService.getEventTypeBreakdown(this.eventData);
      this.levelDistribution = this.siteReportService.getLevelDistribution(this.eventData);
      this.timeDistribution = this.siteReportService.getTimeDistribution(this.eventData);
      this.timeLevelDistribution = this.getTimeLevelDistribution(this.eventData);
      this.cameraDistribution = this.siteReportService.getCameraDistribution(this.eventData);
      this.actionDistribution = this.siteReportService.getActionDistribution(this.eventData);
      this.levelActionLineData = this.siteReportService.getLevelActionLineData(this.eventData);
      this.consolidatedLevelData = this.siteReportService.getConsolidatedLevelActionsData(this.eventData);

      console.log('Data processing results:', {
        eventTypeBreakdown: this.eventTypeBreakdown,
        timeDistribution: this.timeDistribution,
        cameraDistribution: this.cameraDistribution,
        actionDistribution: this.actionDistribution,
        levelActionLineData: this.levelActionLineData,
        consolidatedLevelData: this.consolidatedLevelData
      });

      // Calculate summary statistics
      this.calculateSummaryStats();
      this.cdr.detectChanges(); // Force change detection after data processing
    } catch (error) {
      console.error('Error processing data:', error);
    }
  }

  private calculateSummaryStats(): void {
    const uniqueCameras = new Set(this.eventData.map(item => item.cameraId));
    const uniqueEventTypes = new Set(this.eventData.map(item => item.eventType));
    
    const dates = this.eventData.map(item => {
      const timestampStr = item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring;
      if (timestampStr && timestampStr.trim() !== '') {
        const date = new Date(timestampStr);
        return !isNaN(date.getTime()) ? date.toDateString() : 'Unknown';
      }
      return 'Unknown';
    });
    const uniqueDates = new Set(dates.filter(date => date !== 'Unknown'));
    
    const dateRange = this.getDateRange();

    this.summaryStats = {
      totalEvents: this.eventData.length,
      uniqueCameras: uniqueCameras.size,
      uniqueEventTypes: uniqueEventTypes.size,
      dateRange: dateRange,
      averageEventsPerDay: uniqueDates.size > 0 ? this.eventData.length / uniqueDates.size : 0
    };
    console.log('summaryStats>>', this.summaryStats, 'uniqueDates>>', uniqueDates);
  }

  private getDateRange(): string {
    if (this.eventData.length === 0) return '';

    const validDates = this.eventData
      .map(item => {
        const timestampStr = item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring;
        if (timestampStr && timestampStr.trim() !== '') {
          const date = new Date(timestampStr);
          return !isNaN(date.getTime()) ? date : null;
        }
        return null;
      })
      .filter(date => date !== null);

    if (validDates.length === 0) return 'No valid dates';

    const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));

    return `${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;
  }

  private getTimeLevelDistribution(data: EventData[]): any {
    const timeLevelMap = new Map<string, Map<string, number>>();
    
    // Initialize all 24 hours
    for (let hour = 0; hour < 24; hour++) {
      const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
      timeLevelMap.set(timeSlot, new Map());
    }

    // Count events by time and level
    data.forEach(item => {
      try {
        let timestampStr = item.eventtimestampstr;
        if (!timestampStr || timestampStr.trim() === '') {
          return;
        }
        
        const timestamp = new Date(timestampStr);
        if (isNaN(timestamp.getTime())) {
          return;
        }
        
        const hour = timestamp.getHours();
        const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
        const level = item.currentLevelId || item.eventLevel || 'No Level';
        
        const levelMap = timeLevelMap.get(timeSlot);
        if (levelMap) {
          levelMap.set(level, (levelMap.get(level) || 0) + 1);
        }
      } catch (error) {
        console.warn('Error processing timestamp for item:', item.eventId, error);
      }
    });

    // Get all unique levels
    const allLevels = new Set<string>();
    timeLevelMap.forEach(levelMap => {
      levelMap.forEach((count, level) => {
        allLevels.add(level);
      });
    });

    const levelArray = Array.from(allLevels).sort();
    const timeArray = Array.from(timeLevelMap.keys()).sort((a, b) => {
      const hourA = parseInt(a.split(':')[0]);
      const hourB = parseInt(b.split(':')[0]);
      return hourA - hourB;
    });

    // Create datasets for each level
    const datasets = levelArray.map(level => {
      const data = timeArray.map(timeSlot => {
        const levelMap = timeLevelMap.get(timeSlot);
        return levelMap ? (levelMap.get(level) || 0) : 0;
      });
      
      return {
        label: level,
        data: data,
        backgroundColor: this.generateColors(levelArray.length)[levelArray.indexOf(level)],
        borderColor: this.generateColors(levelArray.length)[levelArray.indexOf(level)],
        borderWidth: 1
      };
    });

    return {
      labels: timeArray,
      datasets: datasets
    };
  }

  private updateCharts(): void {
    try {
      console.log('Starting chart updates...');
      
      // Destroy existing charts
      this.destroyCharts();
      
      // Wait for DOM to be ready
      setTimeout(() => {
        this.createCharts();
      }, 100);
      
      console.log('Charts updated successfully');
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error updating charts:', error);
    }
  }

  private destroyCharts(): void {
    if (this.eventTypeChart) {
      this.eventTypeChart.destroy();
      this.eventTypeChart = null;
    }
    if (this.timeDistributionChart) {
      this.timeDistributionChart.destroy();
      this.timeDistributionChart = null;
    }
    if (this.timeLevelStackedChart) {
      this.timeLevelStackedChart.destroy();
      this.timeLevelStackedChart = null;
    }
    if (this.cameraDistributionChart) {
      this.cameraDistributionChart.destroy();
      this.cameraDistributionChart = null;
    }
    if (this.levelDistributionChart) {
      this.levelDistributionChart.destroy();
      this.levelDistributionChart = null;
    }
    if (this.actionDistributionChart) {
      this.actionDistributionChart.destroy();
      this.actionDistributionChart = null;
    }
    if (this.levelActionLineChart) {
      this.levelActionLineChart.destroy();
      this.levelActionLineChart = null;
    }
    if (this.consolidatedLevelChart) {
      this.consolidatedLevelChart.destroy();
      this.consolidatedLevelChart = null;
    }
  }

  private createCharts(): void {
    try {
      // Create Event Type Pie Chart
      if (this.eventTypeBreakdown && this.eventTypeBreakdown.length > 0 && this.eventTypeCanvas) {
        const labels = this.eventTypeBreakdown.map(item => item.type || 'Unknown');
        const data = this.eventTypeBreakdown.map(item => item.count || 0);
        const colors = this.generateColors(labels.length);

        this.eventTypeChart = new Chart(this.eventTypeCanvas.nativeElement, {
          type: 'pie',
          data: {
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: colors,
              borderColor: colors,
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right'
              },
              title: {
                display: true,
                text: 'Event Type Distribution'
              }
            }
          }
        });
      }

      // Create Time Distribution Bar Chart
      if (this.timeDistribution && this.timeDistribution.length > 0 && this.timeDistributionCanvas) {
        const timeLabels = this.timeDistribution.map(item => item.time || 'Unknown');
        const timeData = this.timeDistribution.map(item => item.count || 0);

        this.timeDistributionChart = new Chart(this.timeDistributionCanvas.nativeElement, {
          type: 'bar',
          data: {
            labels: timeLabels,
            datasets: [{
              label: 'Events by Hour',
              data: timeData,
              backgroundColor: '#36A2EB',
              borderColor: '#2693E6',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              title: {
                display: true,
                text: 'Events by Time of Day'
              }
            },
            scales: {} as any
          }
        });
      }

      // Create Time Level Stacked Bar Chart
      if (this.timeLevelDistribution && this.timeLevelDistribution.datasets && this.timeLevelDistribution.datasets.length > 0 && this.timeLevelStackedCanvas) {
        this.timeLevelStackedChart = new Chart(this.timeLevelStackedCanvas.nativeElement, {
          type: 'bar',
          data: {
            labels: this.timeLevelDistribution.labels,
            datasets: this.timeLevelDistribution.datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top'
              },
              title: {
                display: true,
                text: 'Events by Time of Day with Level'
              }
            },
            scales: {
              x: {
                stacked: true,
                title: {
                  display: true,
                  text: 'Time of Day'
                }
              },
              y: {
                stacked: true,
                title: {
                  display: true,
                  text: 'Number of Events'
                }
              }
            } as any
          }
        });
      }

      // Create Camera Distribution Doughnut Chart
      if (this.cameraDistribution && this.cameraDistribution.length > 0 && this.cameraDistributionCanvas) {
        const cameraLabels = this.cameraDistribution.map(item => item.camera || 'Unknown');
        const cameraData = this.cameraDistribution.map(item => item.count || 0);
        const cameraColors = this.generateColors(cameraLabels.length);

        this.cameraDistributionChart = new Chart(this.cameraDistributionCanvas.nativeElement, {
          type: 'doughnut',
          data: {
            labels: cameraLabels,
            datasets: [{
              data: cameraData,
              backgroundColor: cameraColors,
              borderColor: cameraColors,
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              title: {
                display: true,
                text: 'Events by Camera'
              }
            }
          }
        });
      }

      // Create Level Distribution Pie Chart
      if (this.levelDistribution && this.levelDistribution.length > 0 && this.levelDistributionCanvas) {
        const levelLabels = this.levelDistribution.map(item => item.level || 'Unknown');
        const levelData = this.levelDistribution.map(item => item.count || 0);
        const levelColors = this.generateColors(levelLabels.length);

        this.levelDistributionChart = new Chart(this.levelDistributionCanvas.nativeElement, {
          type: 'pie',
          data: {
            labels: levelLabels,
            datasets: [{
              data: levelData,
              backgroundColor: levelColors,
              borderColor: levelColors,
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right'
              },
              title: {
                display: true,
                text: 'Events by Current Level ID'
              }
            }
          }
        });
      }

      // Create Action Distribution Doughnut Chart
      if (this.actionDistribution && this.actionDistribution.length > 0 && this.actionDistributionCanvas) {
        const actionLabels = this.actionDistribution.map(item => item.action || 'Unknown');
        const actionData = this.actionDistribution.map(item => item.count || 0);
        const actionColors = this.generateColors(actionLabels.length);

        this.actionDistributionChart = new Chart(this.actionDistributionCanvas.nativeElement, {
          type: 'doughnut',
          data: {
            labels: actionLabels,
            datasets: [{
              data: actionData,
              backgroundColor: actionColors,
              borderColor: actionColors,
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              title: {
                display: true,
                text: 'Events by Action'
              }
            }
          }
        });
      }

      // Create Level Action Line Chart
      if (this.levelActionLineData && this.levelActionLineData.datasets && this.levelActionLineData.datasets.length > 0 && this.levelActionLineCanvas) {
        this.levelActionLineChart = new Chart(this.levelActionLineCanvas.nativeElement, {
          type: 'line',
          data: {
            labels: this.levelActionLineData.labels,
            datasets: this.levelActionLineData.datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top'
              },
              title: {
                display: true,
                text: 'Events by Level and Action Over Time'
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Time of Day'
                }
              },
              y: {
                title: {
                  display: true,
                  text: 'Number of Events'
                },
                beginAtZero: true
              }
            } as any
          }
        });
      }

      // Create Consolidated Level Actions Line Chart
      if (this.consolidatedLevelData && this.consolidatedLevelData.datasets && this.consolidatedLevelData.datasets.length > 0 && this.consolidatedLevelCanvas) {
        this.consolidatedLevelChart = new Chart(this.consolidatedLevelCanvas.nativeElement, {
          type: 'line',
          data: {
            labels: this.consolidatedLevelData.labels,
            datasets: this.consolidatedLevelData.datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top'
              },
              title: {
                display: true,
                text: 'Consolidated Events by Level Over Time'
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Time of Day'
                }
              },
              y: {
                title: {
                  display: true,
                  text: 'Total Events per Level'
                },
                beginAtZero: true
              }
            } as any
          }
        });
      }

      console.log('Charts created successfully');
      
      // Notify parent component that charts are ready
      this.chartsReady = true;
      this.cdr.detectChanges();
      
      // Emit event to notify parent
      if (this.chartsReady) {
        setTimeout(() => {
          console.log('Charts are now ready for display');
        }, 500);
      }
    } catch (error) {
      console.error('Error creating charts:', error);
    }
  }

  // Helper method to generate colors array
  private generateColors(count: number): string[] {
    const baseColors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  }



}
